<template>
  <RequestWizardStep name="Other Details"
    :complete="isValid"
  >
    <VeeValidateWrapper
      @validation-failed="isValid = false"
      @validation-passed="isValid = true"
      slim
    >
      <ContentCard title="The FOIA permits HRSA to charge fees to FOIA requesters.">
        <p class="lead">For noncommercial requesters, HRSA may charge only for the actual cost of
          searching for records and the cost of making copies. HRSA does not charge a duplication
          fee when providing electronic records.</p>

        <AlertMessage type="info">
          <p>HRSA will not charge if the total processing cost is less than $25.
            If the total processing cost is estimated to exceed the amount specified below,
            HRSA will notify you before proceeding with processing the request.
            If a fee waiver is denied, HRSA will contact you to determine if you would like
            to proceed with your request.
          </p>

          For more information on processing fees, please read the
          <a target="_blank" href="https://www.foia.gov/faq.html"
          >FOIA FAQs</a>.
        </AlertMessage>

        <StepSelection class="row">
          <RadioButtons
            :options="[
              {
                value: false,
                label: 'I am willing to pay fees for this request up to the following amount:'
              },
              {
                value: true,
                label: 'I request a waiver or reduction of all fees for this request.'
              }
            ]"
            class="col-lg-6"
            required
            v-model="feeWaiverRequested"
          />

          <div class="col-lg-6 row ms-3 ms-lg-0 mt-3 mt-lg-0">
            <div v-show-slide="feeWaiverRequested === false"
              class="w-auto"
            >
              <CurrencyBox
                label="Max Fee Amount"
                :rules="{ required: feeWaiverRequested === false }"
                :validationAttrs="{ mode: 'aggressive' }"
                v-model.number="maxFeeAmount"
                v-focus="focusOnFeeAmount"
              />
            </div>

            <div v-show-slide="feeWaiverRequested">
              <TextArea
                label="Waiver Justification"
                class="mb-3"
                rows="3"
                :rules="{
                  required: feeWaiverRequested,
                  max: 2500
                }"
                :validationAttrs="{ mode: 'aggressive' }"
                v-model="feeWaiverJustification"
                v-focus="focusOnWaiverJustification"
              />
            </div>
          </div>
        </StepSelection>

        <ReflectAttrs
          v-bind="{ expProcRequestedLabel: 'I request expedited processing.' }"
          v-slot="{ expProcRequestedLabel }"
        >
          <div>
            <p class="lead">{{ expProcRequestedLabel }}</p>

            <AlertMessage type="info">
              Expedited processing will be granted only under specific circumstances.
              Please see the
              <a target="_blank"
                href="https://www.foia.gov/faq.html"
              >FOIA FAQs</a> to determine if you qualify for expedited processing.
              If you believe these circumstances apply, then select "Yes" below and provide
              a justification.
            </AlertMessage>

            <StepSelection class="row">
              <RadioButtons
                :aria-label="expProcRequestedLabel"
                :options="[
                  { value: false, label: 'No' },
                  { value: true, label: 'Yes' }
                ]"
                class="col-lg-6"
                required
                v-model="expeditedProcessingRequested"
              />

              <div class="col-lg-6 row ms-3 ms-lg-0 mt-3 mt-lg-0">
                <div v-show-slide="expeditedProcessingRequested">
                  <TextArea
                    label="Expedition Justification"
                    class="mb-3"
                    rows="3"
                    :rules="{
                      required: expeditedProcessingRequested,
                      max: 2500
                    }"
                    :validationAttrs="{ mode: 'aggressive' }"
                    v-model="expeditionJustification"
                    v-focus="focusOnExpeditionJustification"
                  />
                </div>
              </div>
            </StepSelection>
          </div>
        </ReflectAttrs>
      </ContentCard>
    </VeeValidateWrapper>
  </RequestWizardStep>
</template>

<script>
import subComponent from '@ssi/vue-common/src/mixins/subComponent';
import AlertMessage from '@/components/AlertMessage';
import { CurrencyBox, TextArea } from '@/components/formFields/common';
import RadioButtons from '@/components/formFields/RadioButtons';
import VeeValidateWrapper from '@/components/VeeValidateWrapper';
import RequestWizardStep from '@/components/requestWizard/RequestWizardStep';
import StepSelection from '@/components/requestWizard/StepSelection';

export default {
  mixins: [subComponent],

  components: {
    AlertMessage,
    CurrencyBox,
    RadioButtons,
    RequestWizardStep,
    StepSelection,
    TextArea,
    VeeValidateWrapper
  },

  data: () => ({
    focusOnExpeditionJustification: false,
    focusOnFeeAmount: false,
    focusOnWaiverJustification: false,
    isValid: false
  }),

  watch: {
    feeWaiverRequested(val) {
      if (val) {
        this.maxFeeAmount = null;
        this.focusOnWaiverJustification = true;
      } else {
        this.feeWaiverJustification = null;
        this.focusOnFeeAmount = true;
      }
    },

    expeditedProcessingRequested(val) {
      if (val) this.focusOnExpeditionJustification = true;
      else this.expeditionJustification = null;
    },
  }
};
</script>
